/**
 * The terrain surfaces: the atlas `scripts/import-terrain-textures.py` builds, sliced into one
 * canvas per recipe and turned into board-anchored patterns. Lighting and tone are baked at
 * import, so loading does no per-texel work.
 */
import type { MaterialId } from "./tileArt";

export const ATLAS_URL = "/terrain/atlas.jpg";
export const ATLAS_INDEX_URL = "/terrain/atlas.json";

type AtlasEntry = { x: number; y: number; size: number; repeat: number };

/** The loaded surfaces. `repeat` is the on-board size in px of one texture tile. */
export type Materials = { get(id: MaterialId): { image: ImageBitmap; repeat: number } };

/** Fetch and slice the atlas. */
export async function loadMaterials(base = ""): Promise<Materials> {
  const [index, blob] = await Promise.all([
    fetch(base + ATLAS_INDEX_URL).then((r) => r.json() as Promise<Record<string, AtlasEntry>>),
    fetch(base + ATLAS_URL).then((r) => r.blob()),
  ]);
  const atlas = await createImageBitmap(blob);
  // One bitmap per recipe: a pattern made from a bitmap is cheap, where one made from a canvas
  // snapshots the canvas every time (measured: 4.3 s of props on landing-zone, most of it that).
  const slices = new Map<string, { image: ImageBitmap; repeat: number }>();
  for (const [id, e] of Object.entries(index)) slices.set(id, { image: await createImageBitmap(atlas, e.x, e.y, e.size, e.size), repeat: e.repeat });
  atlas.close();
  return {
    get(id) {
      const s = slices.get(id);
      if (!s) throw new Error(`terrain atlas has no material "${id}"`);
      return s;
    },
  };
}

let shared: Promise<Materials> | null = null;
let sharedMs: number | null = null;

/** The one atlas load the board layer and the tile swatches share. A failed fetch is retried on the
 *  next call rather than cached forever. */
export function sharedMaterials(): Promise<Materials> {
  if (!shared) {
    const t0 = performance.now();
    shared = loadMaterials().then((m) => {
      sharedMs = performance.now() - t0;
      return m;
    });
    shared.catch(() => (shared = null));
  }
  return shared;
}

/** How long the shared atlas load took, once it has landed. */
export function sharedMaterialsMs(): number | null {
  return sharedMs;
}

export type Ctx2D = OffscreenCanvasRenderingContext2D;

/**
 * A repeating pattern of `id` in board px: anchored to the board origin (shifted by `ox`/`oy`,
 * rotated by `rot` degrees), so neighbouring chunks line up and regions of different tiles don't.
 */
export function pattern(ctx: Ctx2D, materials: Materials, { id, ox = 0, oy = 0, rot = 0, scale = 1 }: { id: MaterialId; ox?: number; oy?: number; rot?: number; scale?: number }): CanvasPattern {
  const m = materials.get(id);
  const p = ctx.createPattern(m.image, "repeat")!;
  const s = (m.repeat * scale) / m.image.width;
  p.setTransform(new DOMMatrix().translate(ox, oy).rotate(rot).scale(s));
  return p;
}
